import { FC, useEffect, useState } from "react";

import { App, Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";

import { IInvoicingByProduct, IPagination, IParams } from "../../../../types";
import { Content } from "../../../../components/Content";
import { PageHeader } from "../../../../components/PageHeader";
import { reports } from "../../../../services/reports";
import { ActionTable } from "../../../../components/ActionTable";
import { ActionFunction } from "../../../../components/ActionColumn";
import { usePagination } from "../../../../hooks";
import { Filter } from "./Filter";
import { NotFound } from "../../../../components/NotFound";
import { api } from "../../../../services/api";

export async function getInvoicingByProduct(params: IParams, filter?: any) {
  const { data } = await api.get<IPagination<IInvoicingByProduct>>(
    `/reports/invoicing-by-product`,
    {
      params: {
        page: params.page,
        perPage: params.perPage,
        ...filter,
      },
    }
  );
  return { data: data.data, meta: data.meta };
}

const InvoicingByProduct: FC = () => {
  const { message } = App.useApp();
  const [initialLoad, setInitialLoad] = useState(false);
  const {
    data,
    isLoading,
    filter,
    setFilter,
    pagination,
    setData,
    handleRefreshData,
  } = usePagination(getInvoicingByProduct, { initialLoad: initialLoad });

  const expandedData =
    initialLoad && data?.length > 0
      ? data.flatMap((item) =>
          item.orders.map((order) => ({
            id: item.id,
            product: order.product,
            quantity: order.quantity,
            price: order.price,
          }))
        )
      : [];

  const columns: ColumnsType<IInvoicingByProduct & { orders: string }> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (value) => {
        return (
          <div style={{ color: "#3030DA" }}>
            {value?.match(/\d/g).slice(0, 4).join("")}
          </div>
        );
      },
      width: 80,
    },
    {
      title: "Peças",
      dataIndex: "product",
      key: "product",
      ellipsis: true,
    },
    {
      title: "Quantidade",
      dataIndex: "quantity",
      key: "quantity",
      ellipsis: true,
    },
    {
      title: "Valor",
      dataIndex: "price",
      key: "price",
      ellipsis: true,
    },
  ];

  const actions: ActionFunction<IInvoicingByProduct> = (record) => {
    return [
      {
        key: "1",
        icon: <PrinterOutlined />,
        label: "Imprimir Registro",
        onClick: () => onPrint(record.id),
      },
    ];
  };

  useEffect(() => {
    const hasFilter =
      !!filter &&
      typeof filter === "object" &&
      !Array.isArray(filter) &&
      Object.keys(filter).length > 0;

    if (hasFilter) {
      setInitialLoad(true);
      return;
    }

    setInitialLoad(false);
    setData([]);
  }, [filter, handleRefreshData, setData]);

  const onPrint = (record?: string) => {
    if (record) {
      reports("invoicing-by-product", { id: record });
      return;
    }
    if (filter && data.length > 0) {
      reports("invoicing-by-product", filter);
      return;
    }
    if (filter && data.length === 0) {
      message.info("Não há registros para impressão.");
      return;
    }
    message.error("Você precisa filtar as informações!");
  };

  return (
    <>
      <PageHeader
        title="Relatório de Faturamento por peça"
        backButton={false}
        actions={[
          <Button
            onClick={() => onPrint()}
            key={"print_table"}
            icon={<PrinterOutlined />}
            type="primary"
            disabled={expandedData?.length === 0}
          >
            Imprimir
          </Button>,
        ]}
        crumb
      />
      <Content margin>
        <Filter onSubmit={(values) => setFilter(values)} filter={filter} />
      </Content>
      <Content margin>
        <ActionTable
          actions={actions}
          pagination={expandedData?.length > 0 ? pagination : false}
          size="small"
          loading={isLoading}
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={expandedData}
          locale={{
            emptyText: <NotFound />,
          }}
        />
      </Content>
    </>
  );
};

export { InvoicingByProduct };
